import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import api from '../services/api';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';

// ── Design system ─────────────────────────────────────────────────────────────

const VERT = 'var(--couleur-bordure-forte)';
const OR = 'var(--couleur-or)';
const FOND = 'var(--couleur-fond)';
const TEXTE = 'var(--couleur-texte)';
const TEXTE_DIM = 'rgba(255,255,255,0.55)';
const BORDURE = 'rgba(255,255,255,0.14)';

// ── Référentiels ──────────────────────────────────────────────────────────────

const ESPECES = [
  { val: 'orignal', fr: 'Orignal', en: 'Moose' },
  { val: 'cerf_virginie', fr: 'Cerf de Virginie', en: 'White-tailed deer' },
  { val: 'ours_noir', fr: 'Ours noir', en: 'Black bear' },
  { val: 'dindon_sauvage', fr: 'Dindon sauvage', en: 'Wild turkey' },
  { val: 'petit_gibier', fr: 'Petit gibier', en: 'Small game' },
  { val: 'oiseaux_migrateurs', fr: 'Oiseaux migrateurs', en: 'Migratory birds' },
  { val: 'coyote', fr: 'Coyote', en: 'Coyote' },
  { val: 'marmotte', fr: 'Marmotte', en: 'Marmot' },
  { val: 'autre', fr: 'Autre', en: 'Other' },
];

const ARMES = [
  { val: 'arme_a_feu', fr: 'Arme à feu', en: 'Firearm' },
  { val: 'arc', fr: 'Arc', en: 'Bow' },
  { val: 'arbalete', fr: 'Arbalète', en: 'Crossbow' },
  { val: 'poudre_noire', fr: 'Poudre noire', en: 'Black powder' },
  { val: 'air_comprime', fr: 'Air comprimé', en: 'Compressed air' },
];

const DUREE_TYPES = [
  { val: 'journee', fr: 'Journée', en: 'Day' },
  { val: 'weekend', fr: 'Weekend', en: 'Weekend' },
  { val: 'saison', fr: 'Saison', en: 'Season' },
  { val: 'year', fr: 'Année', en: 'Year' },
];

// ── Traductions ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Créer un encan',
    sousTitre: 'Mettez votre territoire aux enchères ou à prix fixe',
    accesRefuse: 'Cette page est réservée aux propriétaires et agriculteurs.',
    retourTableau: 'Retour au tableau de bord',
    etape: (n) => `Étape ${n} sur 3`,
    steps: ['Terre & type', 'Détails de la chasse', 'Révision & publication'],

    // Étape 1
    choisirTerre: 'Sélectionner votre terre',
    aucunTerre: "Vous n'avez aucune terre enregistrée.",
    enregistrerTerre: 'Enregistrer une terre',
    typeEncan: "Type d'encan",
    auction: 'Encan (enchère montante)',
    encanDesc: 'Le chasseur le plus offrant remporte la mise',
    prixFixe: 'Prix fixe',
    prixFixeDesc: 'Réservation directe au prix affiché',
    prixPlancher: 'Prix plancher (CAD)',
    prixFixeLabel: 'Prix fixe par chasseur (CAD)',
    placesMax: 'Nombre de places maximum',
    placesAide: 'De 1 à 8 chasseurs simultanés',
    fermetureEncan: "Date et heure de fermeture de l'encan",
    fermetureAide: "Le compte à rebours s'arrête à cet instant précis",
    dateDebut: 'Date de début (publication)',
    dateFin: 'Date de fin (publication)',
    dureeType: 'Durée',

    // Étape 2
    especesCiblees: 'Espèces ciblées',
    armesAutorisees: "Types d'armes autorisées",
    superficieCh: 'Superficie chassable (ha)',
    superficieAide: 'Peut être inférieure à la superficie totale du terrain',
    saisonDebut: 'Début de la saison de chasse',
    saisonFin: 'Fin de la saison de chasse',
    zoneChasse: 'Zone de chasse',
    zoneAuto: 'Détectée depuis les coordonnées GPS de la terre',
    zoneInconnue: 'Aucune zone trouvée pour cette terre',
    zoneRecherche: 'Recherche de la zone…',
    inclusions: 'Inclusions',
    hebergement: 'Hébergement inclus',
    guide: 'Guide inclus',
    notes: 'Notes spécifiques',
    notesPh: 'Ex. : Stationnement à l’entrée nord. Pas de quad les dimanches.',

    // Étape 3
    revision: 'Révision',
    resume: "Résumé de l'encan",
    reglements: 'Règlements MELCCFP/MNRF applicables',
    reglesChargement: 'Chargement des règlements…',
    aucunReglement: 'Aucun règlement available pour cette zone et ces espèces.',
    avertissementSaison:
      '⚠ Vos dates de chasse dépassent la saison légale pour certaines espèces. Vérifiez les règlements ci-dessous.',
    brouillon: 'Sauvegarder comme brouillon',
    publier: "Publier l'encan",
    enregistrement: 'Enregistrement…',

    // Validation
    erreur: 'Une erreur est survenue.',
    succesBrouillon: 'Encan sauvegardé comme brouillon.',
    succesPublier: 'Encan publié avec succès.',
    requis: 'Champ requis',
    selectTerre: 'Sélectionnez une terre.',
    selectType: "Choisissez un type d'encan.",
    prixInvalide: 'Prix invalide',
    placesInvalide: 'Doit être entre 1 et 8',
    fermetureInv: 'La fermeture doit être ultérieure à maintenant',
    aucuneEspece: 'Sélectionnez au moins une espèce.',
    aucuneArme: "Sélectionnez au moins un type d'arme.",
    superficieInv: 'Superficie chassable invalide',

    // Navigation
    precedent: '← Précédent',
    suivant: 'Suivant →',
    annuler: 'Annuler',
  },
  en: {
    title: 'Create an auction',
    sousTitre: 'Auction or list your territory at a fixed price',
    accesRefuse: 'This page is reserved for landowners and farmers.',
    retourTableau: 'Back to dashboard',
    etape: (n) => `Step ${n} of 3`,
    steps: ['Property & type', 'Hunting details', 'Review & publish'],

    choisirTerre: 'Select your property',
    aucunTerre: 'You have no registered properties yet.',
    enregistrerTerre: 'Register a property',
    typeEncan: 'Auction type',
    auction: 'Auction (rising bid)',
    encanDesc: 'Highest bidder wins',
    prixFixe: 'Fixed price',
    prixFixeDesc: 'Direct reservation at listed price',
    prixPlancher: 'Floor price (CAD)',
    prixFixeLabel: 'Fixed price per hunter (CAD)',
    placesMax: 'Maximum number of places',
    placesAide: '1 to 8 simultaneous hunters',
    fermetureEncan: 'Auction closing date and time',
    fermetureAide: 'The countdown stops at this exact moment',
    dateDebut: 'Start date (publication)',
    dateFin: 'End date (publication)',
    dureeType: 'Duration',

    especesCiblees: 'Target species',
    armesAutorisees: 'Allowed weapon types',
    superficieCh: 'Huntable area (ha)',
    superficieAide: 'May be smaller than total property area',
    saisonDebut: 'Hunting season start',
    saisonFin: 'Hunting season end',
    zoneChasse: 'Hunting zone',
    zoneAuto: 'Detected from property GPS coordinates',
    zoneInconnue: 'No zone found for this property',
    zoneRecherche: 'Searching for zone…',
    inclusions: 'Inclusions',
    hebergement: 'Lodging included',
    guide: 'Guide included',
    notes: 'Specific notes',
    notesPh: 'E.g.: Park at north entrance. No ATVs on Sundays.',

    revision: 'Review',
    resume: 'Auction summary',
    reglements: 'Applicable MELCCFP/MNRF regulations',
    reglesChargement: 'Loading regulations…',
    aucunReglement: 'No regulations available for this zone and species.',
    avertissementSaison:
      '⚠ Your hunting dates exceed the legal season for some species. Check regulations below.',
    brouillon: 'Save as draft',
    publier: 'Publish auction',
    enregistrement: 'Saving…',

    erreur: 'An error occurred.',
    succesBrouillon: 'Auction saved as draft.',
    succesPublier: 'Auction published successfully.',
    requis: 'Required field',
    selectTerre: 'Select a property.',
    selectType: 'Choose an auction type.',
    prixInvalide: 'Invalid price',
    placesInvalide: 'Must be between 1 and 8',
    fermetureInv: 'Closing must be in the future',
    aucuneEspece: 'Select at least one species.',
    aucuneArme: 'Select at least one weapon type.',
    superficieInv: 'Invalid huntable area',

    precedent: '← Previous',
    suivant: 'Next →',
    annuler: 'Cancel',
  },
};

// ── Styles partagés ───────────────────────────────────────────────────────────

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: `1px solid ${BORDURE}`,
  borderRadius: '6px',
  color: TEXTE,
  padding: '0.55rem 0.8rem',
  fontSize: '0.9rem',
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: TEXTE_DIM,
  marginBottom: '0.4rem',
  letterSpacing: '0.02em',
};

const erreurInline = {
  marginTop: '0.3rem',
  fontSize: '0.74rem',
  color: 'var(--couleur-texte-erreur)',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function extraireLatLng(terre) {
  if (!terre) return null;
  const loc = terre.localisation;
  if (loc && Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
    const [lng, lat] = loc.coordinates;
    return { lat, lng };
  }
  if (terre.latitude != null && terre.longitude != null) {
    return { lat: Number(terre.latitude), lng: Number(terre.longitude) };
  }
  return null;
}

function formatDateTimeLocale(dateIso, lang) {
  if (!dateIso) return '—';
  try {
    const d = new Date(dateIso);
    return d.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return dateIso;
  }
}

// ── Barre étapes ─────────────────────────────────────────────────────────────

function BarreEtapes({ courante, t }) {
  return (
    <div style={{ display: 'flex', gap: '6px', marginBottom: '2rem' }}>
      {t.steps.map((label, i) => {
        const n = i + 1;
        const active = n === courante;
        const fait = n < courante;
        return (
          <div key={n} style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                height: 4,
                borderRadius: 3,
                background: fait || active ? OR : 'rgba(255,255,255,0.1)',
                marginBottom: '0.5rem',
              }}
            />
            <div
              style={{
                fontSize: '0.72rem',
                color: active ? OR : fait ? TEXTE_DIM : 'rgba(255,255,255,0.3)',
                fontWeight: active ? 700 : 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {n}. {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Étape 1 ──────────────────────────────────────────────────────────────────

function Etape1({ form, setForm, lands, chargementTerres, erreurs, t, lang }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ display: 'grid', gap: '1.2rem' }}>
      <div>
        <label style={labelStyle}>{t.choisirTerre} *</label>
        {chargementTerres ? (
          <div style={{ ...inputStyle, color: TEXTE_DIM }}>…</div>
        ) : lands.length === 0 ? (
          <div
            style={{
              background: 'rgba(184,134,11,0.1)',
              border: `1px solid ${OR}66`,
              borderRadius: '6px',
              padding: '0.75rem 1rem',
              fontSize: '0.85rem',
              color: TEXTE,
            }}
          >
            {t.aucunTerre}{' '}
            <a href="/register-land" style={{ color: OR, fontWeight: 600 }}>
              → {t.enregistrerTerre}
            </a>
          </div>
        ) : (
          <select value={form.land_id} onChange={set('land_id')} style={inputStyle}>
            <option value="">—</option>
            {lands.map((t2) => (
              <option key={t2.id} value={t2.id}>
                {t2.title} · {t2.province} · {t2.area_hectares} ha
              </option>
            ))}
          </select>
        )}
        {erreurs.land_id && <div style={erreurInline}>{erreurs.land_id}</div>}
      </div>

      <div>
        <label style={labelStyle}>{t.typeEncan} *</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7rem' }}>
          {[
            { val: 'enchere', title: t.auction, desc: t.encanDesc },
            { val: 'fixed_price', title: t.prixFixe, desc: t.prixFixeDesc },
          ].map((opt) => {
            const active = form.mode === opt.val;
            return (
              <button
                type="button"
                key={opt.val}
                onClick={() => setForm((f) => ({ ...f, mode: opt.val }))}
                style={{
                  padding: '0.9rem 1rem',
                  background: active ? `${VERT}77` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  color: TEXTE,
                  borderRadius: '8px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                <div
                  style={{
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    color: active ? OR : 'var(--couleur-texte)',
                    marginBottom: '0.3rem',
                  }}
                >
                  {opt.title}
                </div>
                <div style={{ fontSize: '0.76rem', color: TEXTE_DIM }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
        {erreurs.mode && <div style={erreurInline}>{erreurs.mode}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>
            {form.mode === 'fixed_price' ? t.prixFixeLabel : t.prixPlancher} *
          </label>
          <input
            type="number"
            min={0}
            step={0.01}
            value={form.mode === 'fixed_price' ? form.fixed_price : form.floor_price}
            onChange={set(form.mode === 'fixed_price' ? 'fixed_price' : 'floor_price')}
            placeholder="0.00"
            style={inputStyle}
          />
          {erreurs.prix && <div style={erreurInline}>{erreurs.prix}</div>}
        </div>

        <div>
          <label style={labelStyle}>{t.placesMax} *</label>
          <input
            type="number"
            min={1}
            max={8}
            value={form.max_seats}
            onChange={set('max_seats')}
            style={inputStyle}
          />
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.25rem' }}>
            {t.placesAide}
          </div>
          {erreurs.max_seats && <div style={erreurInline}>{erreurs.max_seats}</div>}
        </div>
      </div>

      <div>
        <label style={labelStyle}>{t.fermetureEncan} *</label>
        <input
          type="datetime-local"
          value={form.auction_closes_at}
          onChange={set('auction_closes_at')}
          style={inputStyle}
        />
        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.25rem' }}>
          {t.fermetureAide}
        </div>
        {erreurs.auction_closes_at && <div style={erreurInline}>{erreurs.auction_closes_at}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>{t.dateDebut} *</label>
          <input
            type="date"
            value={form.start_date}
            onChange={set('start_date')}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>{t.dateFin} *</label>
          <input type="date" value={form.end_date} onChange={set('end_date')} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>{t.dureeType} *</label>
          <select value={form.duration_type} onChange={set('duration_type')} style={inputStyle}>
            {DUREE_TYPES.map((d) => (
              <option key={d.val} value={d.val}>
                {d[lang]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {erreurs.dates && <div style={erreurInline}>{erreurs.dates}</div>}
    </div>
  );
}

// ── Étape 2 ──────────────────────────────────────────────────────────────────

function Etape2({ form, setForm, zone, zoneChargement, erreurs, t, lang }) {
  const toggle = (key) => (val) => {
    setForm((f) => {
      const set = new Set(f[key]);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return { ...f, [key]: Array.from(set) };
    });
  };

  return (
    <div style={{ display: 'grid', gap: '1.4rem' }}>
      <div>
        <label style={labelStyle}>{t.especesCiblees} *</label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '0.4rem',
          }}
        >
          {ESPECES.map((esp) => {
            const active = form.targeted_species.includes(esp.val);
            return (
              <label
                key={esp.val}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.7rem',
                  background: active ? `${VERT}55` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.84rem',
                  color: active ? 'var(--couleur-texte)' : TEXTE_DIM,
                  fontWeight: active ? 600 : 500,
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggle('targeted_species')(esp.val)}
                  style={{ accentColor: OR }}
                />
                {esp[lang]}
              </label>
            );
          })}
        </div>
        {erreurs.targeted_species && <div style={erreurInline}>{erreurs.targeted_species}</div>}
      </div>

      <div>
        <label style={labelStyle}>{t.armesAutorisees} *</label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: '0.4rem',
          }}
        >
          {ARMES.map((a) => {
            const active = form.weapon_types.includes(a.val);
            return (
              <label
                key={a.val}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.7rem',
                  background: active ? `${VERT}55` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  color: active ? 'var(--couleur-texte)' : TEXTE_DIM,
                  fontWeight: active ? 600 : 500,
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggle('weapon_types')(a.val)}
                  style={{ accentColor: OR }}
                />
                {a[lang]}
              </label>
            );
          })}
        </div>
        {erreurs.weapon_types && <div style={erreurInline}>{erreurs.weapon_types}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>{t.superficieCh}</label>
          <input
            type="number"
            min={0}
            step={0.1}
            value={form.huntable_area_ha}
            onChange={(e) => setForm((f) => ({ ...f, huntable_area_ha: e.target.value }))}
            style={inputStyle}
            placeholder="0"
          />
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.25rem' }}>
            {t.superficieAide}
          </div>
          {erreurs.huntable_area_ha && <div style={erreurInline}>{erreurs.huntable_area_ha}</div>}
        </div>
        <div>
          <label style={labelStyle}>{t.zoneChasse}</label>
          <div
            style={{
              ...inputStyle,
              background: 'rgba(184,134,11,0.07)',
              border: `1px solid ${OR}55`,
              color: zone ? OR : TEXTE_DIM,
              fontWeight: zone ? 700 : 400,
            }}
          >
            {zoneChargement
              ? t.zoneRecherche
              : zone
                ? `${zone.zone_code} — ${zone.zone_name || ''}`.trim()
                : t.zoneInconnue}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.25rem' }}>
            {t.zoneAuto}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>{t.saisonDebut}</label>
          <input
            type="date"
            value={form.hunt_start_date}
            onChange={(e) => setForm((f) => ({ ...f, hunt_start_date: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>{t.saisonFin}</label>
          <input
            type="date"
            value={form.hunt_end_date}
            onChange={(e) => setForm((f) => ({ ...f, hunt_end_date: e.target.value }))}
            style={inputStyle}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>{t.inclusions}</label>
        <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
          {[
            { key: 'lodging_included', label: t.hebergement, icon: '🏕' },
            { key: 'guide_included', label: t.guide, icon: '⛺' },
          ].map((opt) => {
            const active = form[opt.key];
            return (
              <button
                type="button"
                key={opt.key}
                onClick={() => setForm((f) => ({ ...f, [opt.key]: !f[opt.key] }))}
                style={{
                  padding: '0.55rem 1.1rem',
                  background: active ? `${OR}22` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  color: active ? OR : TEXTE_DIM,
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                }}
              >
                <span>{opt.icon}</span>
                {opt.label} {active ? '✓' : ''}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label style={labelStyle}>{t.notes}</label>
        <textarea
          value={form.specific_notes}
          onChange={(e) => setForm((f) => ({ ...f, specific_notes: e.target.value }))}
          placeholder={t.notesPh}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
        />
      </div>
    </div>
  );
}

// ── Étape 3 — Révision ────────────────────────────────────────────────────────

function ResumeLigne({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '1rem',
        padding: '0.45rem 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span style={{ fontSize: '0.78rem', color: TEXTE_DIM, fontWeight: 500 }}>{label}</span>
      <span
        style={{
          fontSize: '0.85rem',
          color: 'var(--couleur-texte)',
          fontWeight: 600,
          textAlign: 'right',
        }}
      >
        {value || '—'}
      </span>
    </div>
  );
}

function Etape3({ form, terre, zone, rules, reglesChargement, depasseSaison, t, lang }) {
  const especesLabel = form.targeted_species
    .map((v) => ESPECES.find((e) => e.val === v)?.[lang] || v)
    .join(', ');
  const armesLabel = form.weapon_types
    .map((v) => ARMES.find((a) => a.val === v)?.[lang] || v)
    .join(', ');
  const dureeLabel =
    DUREE_TYPES.find((d) => d.val === form.duration_type)?.[lang] || form.duration_type;

  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      <div
        style={{
          background: `${VERT}66`,
          border: `1px solid ${OR}66`,
          borderRadius: '8px',
          padding: '1.2rem 1.4rem',
        }}
      >
        <div
          style={{
            fontSize: '0.72rem',
            color: OR,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '0.6rem',
          }}
        >
          {t.resume}
        </div>

        <ResumeLigne
          label={t.choisirTerre}
          value={terre ? `${terre.title} (${terre.province}, ${terre.area_hectares} ha)` : '—'}
        />
        <ResumeLigne label={t.typeEncan} value={form.mode === 'enchere' ? t.auction : t.prixFixe} />
        <ResumeLigne
          label={form.mode === 'fixed_price' ? t.prixFixeLabel : t.prixPlancher}
          value={
            form.mode === 'fixed_price'
              ? `${form.fixed_price || 0} CAD`
              : `${form.floor_price || 0} CAD`
          }
        />
        <ResumeLigne label={t.placesMax} value={form.max_seats} />
        <ResumeLigne
          label={t.fermetureEncan}
          value={formatDateTimeLocale(form.auction_closes_at, lang)}
        />
        <ResumeLigne label={t.dureeType} value={dureeLabel} />
        <ResumeLigne label={t.especesCiblees} value={especesLabel} />
        <ResumeLigne label={t.armesAutorisees} value={armesLabel} />
        <ResumeLigne
          label={t.superficieCh}
          value={form.huntable_area_ha ? `${form.huntable_area_ha} ha` : '—'}
        />
        <ResumeLigne label={t.saisonDebut} value={form.hunt_start_date || '—'} />
        <ResumeLigne label={t.saisonFin} value={form.hunt_end_date || '—'} />
        <ResumeLigne
          label={t.zoneChasse}
          value={zone ? `${zone.zone_code} — ${zone.zone_name || ''}` : t.zoneInconnue}
        />
        <ResumeLigne label={t.hebergement} value={form.lodging_included ? '✓' : '—'} />
        <ResumeLigne label={t.guide} value={form.guide_included ? '✓' : '—'} />
        {form.specific_notes && <ResumeLigne label={t.notes} value={form.specific_notes} />}
      </div>

      {depasseSaison && (
        <div
          style={{
            background: 'rgba(220,160,40,0.12)',
            border: '1px solid rgba(220,160,40,0.55)',
            borderRadius: '6px',
            padding: '0.7rem 1rem',
            color: 'var(--couleur-texte-warning)',
            fontSize: '0.85rem',
          }}
        >
          {t.avertissementSaison}
        </div>
      )}

      <div>
        <div
          style={{
            fontSize: '0.72rem',
            color: OR,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            marginBottom: '0.7rem',
          }}
        >
          {t.reglements}
        </div>

        {reglesChargement ? (
          <div style={{ color: TEXTE_DIM, fontSize: '0.85rem' }}>{t.reglesChargement}</div>
        ) : !zone || rules.length === 0 ? (
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${BORDURE}`,
              borderRadius: '6px',
              padding: '0.8rem 1rem',
              color: TEXTE_DIM,
              fontSize: '0.82rem',
            }}
          >
            {t.aucunReglement}
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '0.5rem' }}>
            {rules.map((r, i) => (
              <div
                key={r.id || i}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${BORDURE}`,
                  borderRadius: '6px',
                  padding: '0.6rem 0.85rem',
                }}
              >
                <div
                  style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--couleur-texte)' }}
                >
                  {lang === 'fr' ? r.species_fr : r.species_en || r.species_fr}
                </div>
                <div style={{ fontSize: '0.74rem', color: TEXTE_DIM, marginTop: '2px' }}>
                  {r.weapon_type && <span>{r.weapon_type} · </span>}
                  {r.season_type && <span>{r.season_type} · </span>}
                  {r.start_date && r.end_date && (
                    <span>
                      {r.start_date} → {r.end_date}
                    </span>
                  )}
                </div>
                {r.daily_limit && (
                  <div style={{ fontSize: '0.72rem', color: OR, marginTop: '3px' }}>
                    Limite/jour : {r.daily_limit}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Validation ────────────────────────────────────────────────────────────────

function validerEtape(n, form, t) {
  const e = {};
  if (n === 1) {
    if (!form.land_id) e.land_id = t.selectTerre;
    if (!form.mode) e.mode = t.selectType;

    const prix = form.mode === 'fixed_price' ? form.fixed_price : form.floor_price;
    const p = parseFloat(prix);
    if (!prix || isNaN(p) || p <= 0) e.prix = t.prixInvalide;

    const places = parseInt(form.max_seats, 10);
    if (isNaN(places) || places < 1 || places > 8) e.max_seats = t.placesInvalide;

    if (!form.auction_closes_at) {
      e.auction_closes_at = t.requis;
    } else if (new Date(form.auction_closes_at) <= new Date()) {
      e.auction_closes_at = t.fermetureInv;
    }

    if (
      !form.start_date ||
      !form.end_date ||
      new Date(form.start_date) >= new Date(form.end_date)
    ) {
      e.dates = t.requis;
    }
  }
  if (n === 2) {
    if (form.targeted_species.length === 0) e.targeted_species = t.aucuneEspece;
    if (form.weapon_types.length === 0) e.weapon_types = t.aucuneArme;

    if (form.huntable_area_ha !== '' && form.huntable_area_ha != null) {
      const v = parseFloat(form.huntable_area_ha);
      if (isNaN(v) || v < 0) e.huntable_area_ha = t.superficieInv;
    }
  }
  return e;
}

// ── Composant principal ──────────────────────────────────────────────────────

const FORM_INIT = {
  land_id: '',
  mode: 'enchere',
  floor_price: '',
  fixed_price: '',
  max_seats: 1,
  auction_closes_at: '',
  start_date: '',
  end_date: '',
  duration_type: 'saison',
  targeted_species: [],
  weapon_types: [],
  huntable_area_ha: '',
  hunt_start_date: '',
  hunt_end_date: '',
  lodging_included: false,
  guide_included: false,
  specific_notes: '',
};

export default function CreerEncan() {
  const { lang } = useContext(LangContext);
  const { utilisateur, estConnecte } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pre-select a land when arriving from the wizard:
  //   /create-auction?land=<uuid>
  // This skips the "pick a land" step entirely. The user can still change the
  // selection in the form's <select> — it's a default, not a lock.
  const landPrechoisi = searchParams.get('land') || '';
  const t = T[lang];

  const [etape, setEtape] = useState(1);
  const [form, setForm] = useState(() => ({ ...FORM_INIT, land_id: landPrechoisi }));
  const [erreurs, setErreurs] = useState({});

  const [lands, setTerres] = useState([]);
  const [chargementTerres, setChargementTerres] = useState(true);

  const [zone, setZone] = useState(null);
  const [zoneChargement, setZoneChargement] = useState(false);

  const [rules, setRegles] = useState([]);
  const [reglesChargement, setReglesChargement] = useState(false);

  const [envoi, setEnvoi] = useState(false);
  const [erreurApi, setErreurApi] = useState('');
  const [succes, setSucces] = useState('');

  // ── Garde rôle ─────────────────────────────────────────────────────────
  const roleAutorise = useMemo(() => {
    const r = utilisateur?.user_type;
    return r === 'landowner' || r === 'farmer';
  }, [utilisateur]);

  // ── Chargement de mes lands ──────────────────────────────────────────
  useEffect(() => {
    if (!estConnecte || !roleAutorise) return;
    setChargementTerres(true);
    api
      .get('/lands/mes-lands')
      .then((res) => setTerres(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTerres([]))
      .finally(() => setChargementTerres(false));
  }, [estConnecte, roleAutorise]);

  // ── Lookup zone dès qu'une terre est sélectionnée ─────────────────────
  const terreSelectionnee = lands.find((t2) => t2.id === form.land_id);

  useEffect(() => {
    if (!terreSelectionnee) {
      setZone(null);
      return;
    }

    // Si la terre a déjà hunting_zone_id, on l'utilise directement
    if (terreSelectionnee.hunting_zone_id) {
      setZone({
        zone_code: terreSelectionnee.hunting_zone_id,
        zone_name: '',
        province: terreSelectionnee.province,
      });
      return;
    }

    const coord = extraireLatLng(terreSelectionnee);
    if (!coord) {
      setZone(null);
      return;
    }

    setZoneChargement(true);
    api
      .post('/zones/lookup', { lat: coord.lat, lng: coord.lng })
      .then((res) => setZone(res.data))
      .catch(() => setZone(null))
      .finally(() => setZoneChargement(false));
  }, [terreSelectionnee]);

  // ── Chargement des règlements quand on entre à l'étape 3 ──────────────
  useEffect(() => {
    if (etape !== 3 || !zone?.zone_code) {
      setRegles([]);
      return;
    }
    setReglesChargement(true);
    api
      .get(`/zones/${zone.zone_code}/rules`)
      .then((res) => setRegles(res.data?.rules || []))
      .catch(() => setRegles([]))
      .finally(() => setReglesChargement(false));
  }, [etape, zone]);

  // ── Détection : les dates de chasse dépassent-elles les saisons légales ?
  const depasseSaison = useMemo(() => {
    if (!form.hunt_start_date || !form.hunt_end_date || rules.length === 0) {
      return false;
    }
    const debutChasse = new Date(form.hunt_start_date);
    const finChasse = new Date(form.hunt_end_date);
    return rules.some((r) => {
      if (!r.start_date || !r.end_date) return false;
      const rd = new Date(r.start_date);
      const rf = new Date(r.end_date);
      // Dépasse = la fenêtre de chasse contient des dates hors saison légale
      return debutChasse < rd || finChasse > rf;
    });
  }, [form.hunt_start_date, form.hunt_end_date, rules]);

  // ── Garde de rendu ─────────────────────────────────────────────────────
  if (!estConnecte) return <Navigate to="/login" replace />;
  if (utilisateur && !roleAutorise) {
    return (
      <PrivateLayout>
        <div style={{ maxWidth: 600, margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫</div>
          <h1 style={{ fontFamily: 'Georgia, serif', color: 'var(--couleur-texte)' }}>
            {t.accesRefuse}
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              marginTop: '1.5rem',
              background: OR,
              color: 'var(--couleur-fond-carte)',
              border: 'none',
              padding: '0.6rem 1.4rem',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ← {t.retourTableau}
          </button>
        </div>
      </PrivateLayout>
    );
  }

  // ── Navigation ─────────────────────────────────────────────────────────
  const suivant = () => {
    const e = validerEtape(etape, form, t);
    setErreurs(e);
    if (Object.keys(e).length === 0) setEtape((n) => Math.min(3, n + 1));
  };
  const precedent = () => {
    setErreurs({});
    setEtape((n) => Math.max(1, n - 1));
  };

  // ── Soumission ─────────────────────────────────────────────────────────
  const construirePayload = () => ({
    land_id: form.land_id,
    mode: form.mode,
    floor_price: form.mode === 'enchere' ? Number(form.floor_price) : undefined,
    fixed_price: form.mode === 'fixed_price' ? Number(form.fixed_price) : undefined,
    targeted_species: form.targeted_species,
    weapon_types: form.weapon_types,
    start_date: form.start_date,
    end_date: form.end_date,
    hunt_start_date: form.hunt_start_date || null,
    hunt_end_date: form.hunt_end_date || null,
    auction_closes_at: form.auction_closes_at || null,
    duration_type: form.duration_type,
    huntable_area_ha: form.huntable_area_ha ? Number(form.huntable_area_ha) : null,
    max_seats: Number(form.max_seats) || 1,
    lodging_included: !!form.lodging_included,
    guide_included: !!form.guide_included,
    specific_notes: form.specific_notes || null,
    hunting_zone_id: zone?.zone_code || null,
    country: 'CA',
  });

  const enregistrer = async (commeBrouillon) => {
    // Valider les étapes 1 et 2
    for (const n of [1, 2]) {
      const e = validerEtape(n, form, t);
      if (Object.keys(e).length > 0) {
        setErreurs(e);
        setEtape(n);
        return;
      }
    }
    setEnvoi(true);
    setErreurApi('');
    setSucces('');
    try {
      const payload = { ...construirePayload(), status: commeBrouillon ? 'brouillon' : undefined };
      const res = await api.post('/listings', payload);
      setSucces(commeBrouillon ? t.succesBrouillon : t.succesPublier);
      setTimeout(() => {
        if (res.data?.id) navigate(`/auctions/${res.data.id}`);
        else navigate('/dashboard');
      }, 800);
    } catch (err) {
      setErreurApi(err.response?.data?.error || t.erreur);
    } finally {
      setEnvoi(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <PrivateLayout>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '2.5rem 1.5rem 5rem' }}>
        <div
          style={{
            background: `${VERT}55`,
            border: `1px solid ${OR}55`,
            borderRadius: '10px',
            padding: '1.4rem 1.6rem',
            marginBottom: '2rem',
          }}
        >
          <div
            style={{
              fontSize: '0.72rem',
              color: OR,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              marginBottom: '0.35rem',
            }}
          >
            {t.etape(etape)}
          </div>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: '1.55rem',
              color: 'var(--couleur-texte)',
              margin: '0 0 0.25rem',
            }}
          >
            🏷 {t.title}
          </h1>
          <div style={{ fontSize: '0.85rem', color: TEXTE_DIM }}>{t.sousTitre}</div>
        </div>

        <BarreEtapes courante={etape} t={t} />

        <div
          style={{
            background: 'rgba(255,255,255,0.02)',
            border: `1px solid ${BORDURE}`,
            borderRadius: '10px',
            padding: '1.6rem',
            marginBottom: '1.5rem',
          }}
        >
          {etape === 1 && (
            <Etape1
              form={form}
              setForm={setForm}
              lands={lands}
              chargementTerres={chargementTerres}
              erreurs={erreurs}
              t={t}
              lang={lang}
            />
          )}
          {etape === 2 && (
            <Etape2
              form={form}
              setForm={setForm}
              zone={zone}
              zoneChargement={zoneChargement}
              erreurs={erreurs}
              t={t}
              lang={lang}
            />
          )}
          {etape === 3 && (
            <Etape3
              form={form}
              terre={terreSelectionnee}
              zone={zone}
              rules={rules}
              reglesChargement={reglesChargement}
              depasseSaison={depasseSaison}
              t={t}
              lang={lang}
            />
          )}
        </div>

        {erreurApi && (
          <div
            style={{
              background: 'rgba(200,50,50,0.12)',
              border: '1px solid rgba(200,50,50,0.4)',
              borderRadius: '6px',
              padding: '0.7rem 1rem',
              marginBottom: '1rem',
              color: 'var(--couleur-texte-erreur)',
              fontSize: '0.85rem',
            }}
          >
            ⚠ {erreurApi}
          </div>
        )}
        {succes && (
          <div
            style={{
              background: 'rgba(40,160,80,0.12)',
              border: '1px solid rgba(40,160,80,0.4)',
              borderRadius: '6px',
              padding: '0.7rem 1rem',
              marginBottom: '1rem',
              color: 'var(--couleur-texte-succes)',
              fontSize: '0.85rem',
            }}
          >
            ✓ {succes}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: '0.7rem',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <button
            type="button"
            onClick={precedent}
            disabled={etape === 1 || envoi}
            style={{
              background: 'transparent',
              border: `1px solid ${BORDURE}`,
              color: etape === 1 ? 'rgba(255,255,255,0.2)' : TEXTE,
              padding: '0.6rem 1.3rem',
              borderRadius: '6px',
              fontSize: '0.88rem',
              fontWeight: 500,
              cursor: etape === 1 || envoi ? 'not-allowed' : 'pointer',
              opacity: etape === 1 || envoi ? 0.5 : 1,
            }}
          >
            {t.precedent}
          </button>

          {etape < 3 && (
            <button
              type="button"
              onClick={suivant}
              style={{
                background: OR,
                border: `1px solid ${OR}`,
                color: 'var(--couleur-fond-carte)',
                padding: '0.6rem 1.6rem',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {t.suivant}
            </button>
          )}

          {etape === 3 && (
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => enregistrer(true)}
                disabled={envoi}
                style={{
                  background: 'transparent',
                  border: `1px solid ${OR}`,
                  color: OR,
                  padding: '0.6rem 1.3rem',
                  borderRadius: '6px',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  cursor: envoi ? 'not-allowed' : 'pointer',
                  opacity: envoi ? 0.6 : 1,
                }}
              >
                {envoi ? t.enregistrement : t.brouillon}
              </button>
              <button
                type="button"
                onClick={() => enregistrer(false)}
                disabled={envoi}
                style={{
                  background: OR,
                  border: `1px solid ${OR}`,
                  color: 'var(--couleur-fond-carte)',
                  padding: '0.6rem 1.6rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: envoi ? 'not-allowed' : 'pointer',
                  opacity: envoi ? 0.6 : 1,
                }}
              >
                {envoi ? t.enregistrement : `🏷 ${t.publier}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </PrivateLayout>
  );
}
